import {Component, OnDestroy, OnInit} from '@angular/core';
import {FloorPlanEditorComponent} from '../../../floor-plan-editor/containers/floor-plan-editor/floor-plan-editor.component';
import {MatDialog} from '@angular/material';
import {select, Store} from '@ngrx/store';

import * as fromRoot from '../../../reducers';
import * as fromAfbApi from '../../../afb-api/reducers';
import * as fromFloorPlan from '../../reducers';
import * as fromDashboard from '../../../dashboard/reducers';

import {Observable, combineLatest, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map, take, withLatestFrom} from 'rxjs/operators';
import {GetOrganizationFloors} from '../../../afb-api/actions/floor-plan.actions';
import {OrganizationWithToken} from '../../../core/models/core';
import {Floor} from '../../../afb-api/models/floor';
import {DeselectFloor, SelectAirComponent, SelectFloor} from '../../actions/floor-plan-view.actions';
import {AirComponentType} from '../../../afb-api/enums/air-component-type.enum';
import {DeviceMarkerWithPolledAirDatum} from '../../../floor-plan-shared/models/device-marker';
import {DeviceWithPolledAirDatum, DeviceWithUuid} from '../../../afb-api/models/device';
import {
  AddSelectedDevice,
  EmptySelectedDevice,
  RemoveSelectedDevice,
  SetSelectedDevice
} from '../../../dashboard/actions/dashboard-device.actions';
import {Snapshot} from '../../../shared/models/snapshot';
import {PolledAirDatum} from '../../../afb-api/models/air-data';
import {SelfOrganizationUser} from '../../../afb-api/models/organization-user';

@Component({
  selector: 'awair-floor-plan',
  templateUrl: './floor-plan.component.html',
  styleUrls: ['./floor-plan.component.scss']
})

export class FloorPlanComponent implements OnInit, OnDestroy {

  selectedOrganization$: Observable<OrganizationWithToken> = this.rootStore.pipe(
    select(fromRoot.selectedOrganization),
    filter((selectedOrganization: OrganizationWithToken) => selectedOrganization != null)
  );

  hasFloor$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.hasFloor),
    filter((hasFloor: boolean) => hasFloor != null)
  );

  floorEntities$: Observable<{ [floorId: string]: Floor }> = this.afbApiStore.pipe(
    select(fromAfbApi.floorEntities)
  );

  floors$: Observable<Floor[]> = this.afbApiStore.pipe(
    select(fromAfbApi.floors),
    filter((floors: Floor[]) => floors != null)
  );

  firstFloor$: Observable<Floor> = this.afbApiStore.pipe(
    select(fromAfbApi.floors),
    filter((floors: Floor[]) => floors != null && floors.length > 0),
    map((floors: Floor[]) => floors[0])
  );

  isGetFloorsLoaded$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.isGetFloorsLoaded),
    filter((areFloorsLoaded: boolean) => areFloorsLoaded),
    take(1)
  );

  selectedFloorId$: Observable<string | null> = this.floorPlanStore.pipe(
    select(fromFloorPlan.selectedFloorId),
    distinctUntilChanged()
  );

  // Doing combineLatest to react to both floor list and selectedFloorId changes
  selectedFloor$: Observable<Floor> = combineLatest(
    this.selectedFloorId$,
    this.floorEntities$,
    (selectedFloorId: string, floorEntities: { [floorId: string]: Floor }) => floorEntities[selectedFloorId]
  );

  selectedAirComponent$: Observable<AirComponentType> = this.floorPlanStore.pipe(
    select(fromFloorPlan.selectedAirComponent),
    filter((selectedAirComponent: AirComponentType) => selectedAirComponent != null)
  );

  devicesWithPolledAirData$: Observable<DeviceWithPolledAirDatum[]> = this.afbApiStore.pipe(
    select(fromAfbApi.getDevicesWithPolledAirData),
    filter((devicesWithPolledAirData: DeviceWithPolledAirDatum[]) => devicesWithPolledAirData != null)
  );

  selectedFloorPlanDeviceMarkers$: Observable<DeviceMarkerWithPolledAirDatum[]> = this.afbApiStore.pipe(
    select(fromFloorPlan.selectedFloorPlanDeviceMarkers),
    filter((deviceMarkerWithPolledAirData: DeviceMarkerWithPolledAirDatum[]) => deviceMarkerWithPolledAirData != null)
  );

  // To make snapshots
  selfOrganizationUser$: Observable<SelfOrganizationUser> = this.afbApiStore.pipe(
    select(fromAfbApi.selfOrganizationUser)
  );
  polledAirDataEntities$: Observable<{ [deviceUuid: string]: PolledAirDatum }> = this.afbApiStore.pipe(
    select(fromAfbApi.airDataPollEntities),
    filter((polledAirDataEntities: { [deviceUuid: string]: PolledAirDatum }) => polledAirDataEntities != null)
  );
  selectedDevices$: Observable<DeviceWithUuid[]> = this.dashboardStore.pipe(
    select(fromDashboard.selectedDevicesWithUuid),
    filter((devices: DeviceWithUuid[]) => devices != null)
  );
  selectedDeviceSnapshots$: Observable<Snapshot[]> =
    combineLatest(this.selectedDevices$, this.polledAirDataEntities$, this.selfOrganizationUser$, (
      selectedDevices: DeviceWithUuid[],
      polledAirDataEntities: { [deviceUuid: string]: PolledAirDatum },
      selfOrganizationUser: SelfOrganizationUser) => selectedDevices.reduce((acc: Snapshot[], selectedDevice: DeviceWithUuid) => {
        return [
          ...acc,
          {
            ...polledAirDataEntities[selectedDevice.uuid],
            deviceName: selectedDevice.deviceName,
            deviceType: selectedDevice.deviceType,
            email: selectedDevice.email,
            firstName: selectedDevice.ownerFirstName,
            lastName: selectedDevice.ownerLastName,
            tempUnit: selfOrganizationUser.tempUnit
          }
        ]
      }, [])
    );

  isSidebarOpened: boolean = false;

  private selectedOrganizationSubscription: Subscription;
  private initSubscription: Subscription;

  constructor(private dialog: MatDialog,
              private rootStore: Store<fromRoot.State>,
              private afbApiStore: Store<fromAfbApi.State>,
              private floorPlanStore: Store<fromFloorPlan.State>,
              private dashboardStore: Store<fromDashboard.State>) {
  }

  ngOnInit() {
    this.dashboardStore.dispatch(new EmptySelectedDevice());

    this.selectedOrganizationSubscription = this.selectedOrganization$.subscribe((selectedOrganization: OrganizationWithToken) => {
      this.afbApiStore.dispatch(new GetOrganizationFloors({organizationToken: selectedOrganization.token}));
    });

    // Set default selected floor to first floor if selected floor doesn't exist and has floor
    // Check if selected floor exists when deleted floor
    this.initSubscription = this.floorEntities$
      .pipe(
        withLatestFrom(this.selectedFloorId$, this.hasFloor$, this.firstFloor$),
        filter(([entities, selectedFloorId, hasFloor, firstFloor]: [{ [floorId: string]: Floor }, string, boolean, Floor]) =>
          entities[selectedFloorId] == null
        ),
        map(([entities, selectedFloorId, hasFloor, firstFloor]: [{ [floorId: string]: Floor }, string, boolean, Floor]) => {
          return hasFloor ? firstFloor.id : null;
        })
      )
      .subscribe((floorId: string | null) => {
        if (floorId != null) {
          this.floorPlanStore.dispatch(new SelectFloor({floorId}));
        } else {
          this.floorPlanStore.dispatch(new DeselectFloor());
        }
      });
  }

  ngOnDestroy() {
    if (this.selectedOrganizationSubscription) {
      this.selectedOrganizationSubscription.unsubscribe();
    }
    if (this.initSubscription) {
      this.initSubscription.unsubscribe();
    }
  }

  onOpenEditor(event: Event): void {
    this.dialog.open(FloorPlanEditorComponent, {
      disableClose: false,
      hasBackdrop: false,
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%'
    });
  }

  onSelectAirComponent(airComponent: AirComponentType): void {
    this.floorPlanStore.dispatch(new SelectAirComponent(airComponent));
  }

  onToggleSidebar(isSidebarOpened: boolean): void {
    this.isSidebarOpened = isSidebarOpened;
  }

  onSelectFloor(selectedFloorId: string): void {
    this.dashboardStore.dispatch(new EmptySelectedDevice());
    this.floorPlanStore.dispatch(new SelectFloor({
      floorId: selectedFloorId
    }));
    this.isSidebarOpened = false;
  }

  onSelectDeviceFromList(device: DeviceWithUuid): void {
    if (device.floorId != null) {
      // Switch to floor that the selected device exists
      this.floorPlanStore.dispatch(new SelectFloor({
        floorId: device.floorId
      }));
      this.dashboardStore.dispatch(new SetSelectedDevice({
        deviceUuids: [device.uuid]
      }));
    } else {
      alert('Cannot jump to selected device\'s floor since this is not placed in any floor.');
    }
    this.isSidebarOpened = false;
  }

  onSelectDeviceMarker(deviceUuid: string): void {
    this.dashboardStore.dispatch(new AddSelectedDevice({
      deviceUuid
    }));
  }

  onDeselectDeviceMarker(deviceUuid: string): void {
    this.dashboardStore.dispatch(new RemoveSelectedDevice({
      deviceUuid
    }));
  }

  onCloseSnapshot(): void {
    this.dashboardStore.dispatch(new EmptySelectedDevice());
  }
}
